import { useRouter } from '#imports'
import { type ComputedRef, type Ref, computed } from 'vue'

import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/composables/config/useBuybackConfig'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { insertIf } from '@backmarket/utils/collection/insertIf'

import { STEP_NAMES } from '~/scopes/buyback/constants'
import addressTranslations from '~/scopes/buyback/pages/Address.translations'
import bankTranslations from '~/scopes/buyback/pages/Bank.translations'
import shippingTranslations from '~/scopes/buyback/pages/Shipping.translations'

interface useResaleArgs {
  activeStepName: (typeof STEP_NAMES)[keyof typeof STEP_NAMES]
  hasShippingStep: Ref<boolean>
}

interface step {
  label: string
  name: (typeof STEP_NAMES)[keyof typeof STEP_NAMES]
}

export const useResaleStepper = ({
  activeStepName,
  hasShippingStep,
}: useResaleArgs) => {
  const i18n = useI18n()
  const router = useRouter()
  const buybackConfig = useBuybackConfig()

  const steps: ComputedRef<step[]> = computed(() => [
    ...insertIf(buybackConfig.hasBankStep, [
      {
        label: i18n(bankTranslations.headerTitle),
        name: STEP_NAMES.BANK,
      },
    ]),
    {
      label: i18n(addressTranslations.headerTitle),
      name: STEP_NAMES.ADDRESS,
    },
    ...insertIf(hasShippingStep.value, [
      {
        label: i18n(shippingTranslations.headerTitle),
        name: STEP_NAMES.SHIPPING,
      },
    ]),
  ])

  const activeStepIndex = computed(() =>
    steps.value.findIndex((step) => step.name === activeStepName),
  )

  const hasBackButton = computed(() => activeStepIndex.value > 0)

  const goBack = computed(() => () => {
    if (hasBackButton.value) {
      router.back()
    }
  })

  return {
    steps,
    hasBackButton,
    goBack,
  }
}
